import { Link } from 'react-router-dom';
import IdentityFabric from '../components/IdentityFabric';
import MetricsCounters from '../components/MetricsCounters';

function GlassCard({ title, value, description }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
        {title}
      </div>
      <div
        className="mt-2 text-3xl font-semibold tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {value}
      </div>
      <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{description}</div>
    </div>
  );
}

function Pill({ label }) {
  return (
    <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.02] px-3 py-1 text-xs text-[var(--text-secondary)]">
      {label}
    </div>
  );
}

export default function Home() {
  return (
    <div>
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
        />
        <div className="absolute inset-0 opacity-70">
          <IdentityFabric className="h-full w-full" />
        </div>
        <div className="absolute inset-0 " />

        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 sm:pb-24 sm:pt-20 lg:px-8 lg:pb-28">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-5 flex flex-wrap items-center justify-center gap-2">
              <Pill label="Vendor-agnostic" />
              <Pill label="Architecture-led" />
              <Pill label="Outcome-driven" />
              <Pill label="Human + Machine + API + Workload identities" />
            </div>

            <h1
              className="text-4xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Identity, Data & Key Security
              <br />
              Centre of Excellence
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-[var(--text-secondary)] sm:text-lg">
              Securing Human, Machine, and Digital Identities — from Access to Assurance.
            </p>

            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-trust)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-transform hover:translate-y-[-1px] focus:outline-none focus:ring-2 focus:ring-[var(--accent-cyan)]/60 sm:w-auto"
              >
                Request Identity Risk Assessment
              </Link>
              <Link
                to="/capabilities"
                className="inline-flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-6 py-3 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[var(--accent-trust)]/60 sm:w-auto"
              >
                Explore Identity Use Cases
              </Link>
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-[var(--gradient-card)] p-4 text-left">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                  Control Plane
                </div>
                <div className="mt-2 text-sm text-[var(--text-secondary)]">
                  Identity is the new perimeter.
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[var(--gradient-card)] p-4 text-left">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                  Business Risk
                </div>
                <div className="mt-2 text-sm text-[var(--text-secondary)]">
                  Link identity risk to fraud, breach cost, and compliance impact.
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[var(--gradient-card)] p-4 text-left">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                  Trust Architecture
                </div>
                <div className="mt-2 text-sm text-[var(--text-secondary)]">
                  Zero Trust + cryptographic assurance across environments.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            className="text-2xl font-semibold tracking-tight sm:text-3xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Identity is the New Perimeter
          </h2>
          <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            When identity fails, everything fails.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <GlassCard title="Breaches" value="80%+" description="of breaches involve compromised credentials" />
          <GlassCard title="Breach cost" value="$4.5M" description="average cost of a data breach" />
          <GlassCard title="Containment" value="277 days" description="average time to identify and contain" />
          <GlassCard title="Visibility" value="60%" description="of organizations lack unified identity visibility" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <h2
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
              style={{ fontFamily: "var(--font-display)" }}
            >
              What We Secure
            </h2>
            <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
              Every identity. Every access. Every environment.
            </p>
            <div className="mt-6 grid gap-2 text-sm text-[var(--text-secondary)]">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="font-semibold text-[var(--text-primary)]">Human Identities</div>
                <div className="mt-1">Employees, contractors, Partners, Customers</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="font-semibold text-[var(--text-primary)]">Machine Identities</div>
                <div className="mt-1">Servers, VMs, containers, IoT devices</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="font-semibold text-[var(--text-primary)]">API Identities</div>
                <div className="mt-1">Service accounts, microservices, integrations</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="font-semibold text-[var(--text-primary)]">Workload Identities</div>
                <div className="mt-1">Cloud instances, Kubernetes pods, serverless</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="font-semibold text-[var(--text-primary)]">Data Identities</div>
                <div className="mt-1">Certificates, keys, secrets, tokens</div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-6 backdrop-blur-xl sm:p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              The CoE Advantage
            </div>
            <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
              <div className="grid grid-cols-2">
                <div className="bg-white/[0.02] p-4 text-sm font-semibold">Without CoE</div>
                <div className="bg-white/[0.03] p-4 text-sm font-semibold">With CoE</div>

                {[
                  ["Fragmented identity tools", "Unified identity fabric"],
                  ["Manual provisioning", "Automated lifecycle"],
                  ["Reactive breach response", "Proactive threat detection"],
                  ["Compliance scrambles", "Continuous compliance"],
                  ["Password sprawl", "Passwordless & phishing-resistant"],
                ].map(([a, b]) => (
                  <div key={a} className="contents">
                    <div className="border-t border-white/10 bg-white/[0.02] p-4 text-sm text-[var(--text-secondary)]">
                      {a}
                    </div>
                    <div className="border-t border-white/10 bg-white/[0.03] p-4 text-sm text-[var(--text-primary)]">
                      {b}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            className="text-2xl font-semibold tracking-tight sm:text-3xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Best-of-Breed Identity Stack
          </h2>
          <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Best-fit identity controls, unified under a single trust architecture.
          </p>
        </div>

        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {["RSA", "1Kosmos", "OpenText NetIQ", "Yubico", "Entrust", "eMudhra", "CyberArk"].map((v) => (
            <div
              key={v}
              className="flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-6 text-sm font-semibold tracking-tight text-[var(--text-primary)] transition-transform hover:translate-y-[-1px]"
            >
              {v}
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            className="text-2xl font-semibold tracking-tight sm:text-3xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Industries We Protect
          </h2>
          <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Government, BFSI, Digital & Critical Infrastructure, and Digital Natives.
          </p>
        </div>

        <div className="mt-10 grid gap-4 lg:grid-cols-4">
          {[
            {
              title: "Government",
              desc: "Citizen identity. National trust. Zero compromise.",
              href: "/industries/government",
            },
            {
              title: "BFSI",
              desc: "Secure transactions. Fraud prevention. Regulatory confidence.",
              href: "/industries/bfsi",
            },
            {
              title: "Digital & Critical Infrastructure",
              desc: "OT-friendly controls. Continuity. IT/OT convergence.",
              href: "/industries/infrastructure",
            },
            {
              title: "Digital Natives",
              desc: "Frictionless identity at scale. Cloud-native trust.",
              href: "/industries/digital-natives",
            },
          ].map((c) => (
            <Link
              key={c.title}
              to={c.href}
              className="group rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-6 backdrop-blur-xl transition-transform hover:translate-y-[-2px] focus:outline-none focus:ring-2 focus:ring-[var(--accent-trust)]/60"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                Industry
              </div>
              <div
                className="mt-3 text-xl font-semibold tracking-tight"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {c.title}
              </div>
              <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{c.desc}</div>
              <div className="mt-4 text-sm font-semibold text-[var(--text-accent)]">
                Explore
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <h2
                className="text-2xl font-semibold tracking-tight sm:text-3xl"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Trust Metrics
              </h2>
              <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
                Measurable improvements across security posture, operational efficiency, and audit readiness.
              </p>
            </div>
            <MetricsCounters />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 pt-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-white/10 bg-[var(--gradient-trust)] p-10 text-center">
          <h2
            className="text-2xl font-semibold tracking-tight sm:text-3xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Build a Trust-First Digital Enterprise
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Identity is the foundation of digital trust. Let us help you build it right.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-trust)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-transform hover:translate-y-[-1px] sm:w-auto"
            >
              Start Identity Maturity Assessment
            </Link>
            <Link
              to="/contact"
              className="inline-flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-6 py-3 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:bg-white/[0.05] sm:w-auto"
            >
              Schedule Executive Workshop
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
